import { useCallback, useState } from 'react';

import { formatPhoneNumberForRippling } from '@/rippling/util/ripplingDataHelpers';
import { type RipplingPayloadWrapper, sendRipplingDemoTimeEvent } from '@/rippling/util/sendRipplingEvent';
import { getAuthUser } from '@/shared/auth/selectors';
import { useWiwSelector } from '@/store';

interface OpenConfig {
  requirePhoneCollection?: boolean;
}

/**
 * Complete payload data ready for submission to the Growth API.
 * Stored when phone collection is needed so submission can happen after.
 */
export interface PendingSubmissionData {
  payload: RipplingPayloadWrapper;
}

export function useRipplingDemo() {
  const user = useWiwSelector(getAuthUser);
  const [showDemo, setShowDemo] = useState(false);
  const [requirePhoneCollection, setRequirePhoneCollection] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<PendingSubmissionData | null>(null);

  const open = useCallback((config?: OpenConfig) => {
    const requirePhone = config?.requirePhoneCollection ?? false;
    setRequirePhoneCollection(requirePhone);
    setShowDemo(true);
  }, []);

  const closeDemo = useCallback(() => {
    setShowDemo(false);
  }, []);

  /**
   * Called by RipplingDemoOverlay when a booking is confirmed and phone collection is required.
   * Stores the complete submission data so the Growth API call can be made after phone collection.
   */
  const setPendingBookingData = useCallback((data: PendingSubmissionData) => {
    setPendingSubmission(data);
    setShowDemo(false);
  }, []);

  /**
   * Called when phone collection completes. Submits the stored payload to the Growth API,
   * optionally updating the phone number in the payload.
   * @param phone - The collected phone number, or null if dismissed without providing one
   */
  const submitPendingBooking = useCallback(
    (phone: string | null) => {
      if (pendingSubmission) {
        const payload = { ...pendingSubmission.payload };

        // Update the phone in the payload if one was collected, formatting it for Rippling
        if (phone && payload.data) {
          payload.data.Phone = formatPhoneNumberForRippling(phone, user?.country_code);
        }

        sendRipplingDemoTimeEvent(payload);
        setPendingSubmission(null);
      }
    },
    [pendingSubmission, user?.country_code],
  );

  /**
   * Whether we have a pending booking waiting for phone collection.
   */
  const needsPhoneCollection = pendingSubmission !== null;

  return {
    isDemoOpen: showDemo,
    needsPhoneCollection,
    requirePhoneCollection,
    open,
    closeDemo,
    setPendingBookingData,
    submitPendingBooking,
  };
}
